import { evaluateOtFormula, readableOtFormula } from "./otFormulaModel";

export const EXTRA_WORK_PATH = "/salary-v2/extra-work";
/** Premium Works (Sunday / holiday / multipliers / Comp-Off) — same path, OT removed to Overtime Works. */
export const PREMIUM_WORKS_PATH = "/salary-v2/extra-work";
/** Overtime Works — OT enable, hours source, pay calculation, eligibility. */
export const OVERTIME_WORKS_PATH = "/salary-v2/overtime";
export const OVERTIME_FORMULA_BUILDER_PATH = "/salary-v2/overtime/formula";

export const SUNDAY_METHODS = [
  { value: "normal_salary_comp_off", label: "Normal Salary + Compensatory Off" },
  { value: "normal_salary_ot", label: "Normal Salary + Overtime Pay" },
  { value: "double_salary", label: "Double Salary" },
  { value: "double_salary_comp_off", label: "Double Salary + Compensatory Off" },
  { value: "normal_salary_only", label: "Normal Salary Only" },
  { value: "ot_comp_off", label: "Overtime Pay + Compensatory Off" },
  { value: "ot_only", label: "Overtime Pay Only" },
  { value: "double_ot_rate", label: "Double Overtime Rate" },
  { value: "custom", label: "Custom Organization Policy" },
];

export const HOLIDAY_METHODS = [
  { value: "normal_salary_comp_off", label: "Normal Salary + Compensatory Off" },
  { value: "normal_salary_ot", label: "Normal Salary + Overtime Pay" },
  { value: "double_salary", label: "Double Salary" },
  { value: "double_salary_comp_off", label: "Double Salary + Compensatory Off" },
  { value: "ot_only", label: "Overtime Pay Only" },
  { value: "normal_salary_only", label: "Normal Salary Only" },
  { value: "ot_comp_off", label: "Overtime + Compensatory Off" },
  { value: "custom", label: "Custom Organization Policy" },
];

/** OT Pay Calculation methods — business language (no "Multiplier" label). */
export const OT_METHODS = [
  { value: "percentage_of_component", label: "Percentage Based" },
  { value: "hourly_rate", label: "Hourly Rate Based" },
  { value: "fixed_rate", label: "Fixed Amount Based" },
  { value: "formula", label: "Custom Formula" },
];

/** How worked overtime becomes the unit payroll counts. */
export const OT_ALLOCATION_METHODS = [
  {
    value: "hours",
    label: "Count by hours",
    kicker: "1 HR = 1 OT",
    rule: "Every extra hour is one OT hour. 1.1 hours stays 1.1 OT hours.",
    exampleTitle: "1.1 hours worked",
    exampleResult: "1.1 OT hours",
    exampleDetail: "Payroll calculates the exact hours.",
  },
  {
    value: "ot_count",
    label: "Count by OT",
    kicker: "1 OT = 1 OT",
    rule: "Each OT entry counts as one OT. The hours stay saved on that count.",
    exampleTitle: "1.1 hours worked",
    exampleResult: "1 OT",
    exampleDetail: "Hours recorded on the count: 1.1",
  },
  {
    value: "custom",
    label: "Custom OT count",
    kicker: "Your blocks",
    rule: "Add hour blocks, such as every 1 hour = 1 OT or every 2 hours = 1 OT.",
    exampleTitle: "2.2 hours worked",
    exampleResult: "Depends on the block you add",
    exampleDetail: "A partial block stays on the record and is not an extra OT.",
  },
];

export const DEFAULT_OT_COUNT_RULES = [
  { id: "rule-1h", name: "1 hour block", hours: 1, ot_count: 1 },
  { id: "rule-2h", name: "2 hour block", hours: 2, ot_count: 1 },
];

export const OT_HOURS_SOURCES = [
  {
    value: "automatic",
    label: "Automatic from Attendance",
    hint: "Brixigo calculates eligible OT hours from employee shift/schedule and attendance timing.",
    checks: ["OT hours will be calculated from the employee's applicable shift and attendance."],
  },
  {
    value: "manual",
    label: "Manual Entry",
    hint: "OT hours are entered manually by authorized HR/Manager/Admin users.",
    checks: [
      "OT hours will NOT be calculated automatically.",
      "Authorized users must enter OT hours in Attendance.",
    ],
  },
];

export const SALARY_COMPONENTS = [
  { value: "BASIC", label: "Basic Salary" },
  { value: "GROSS", label: "Gross Salary" },
  { value: "BASIC_DA", label: "Basic + DA" },
  { value: "SELECTED", label: "Selected Salary Components" },
  { value: "CTC", label: "CTC Component" },
  { value: "OTHER", label: "Other configurable earnings" },
];

export const PERCENTAGE_APPLIED_ON = [
  { value: "monthly_salary", label: "Monthly Salary" },
  { value: "daily_salary", label: "Daily Salary" },
  { value: "per_ot_hour", label: "Per OT Hour" },
  { value: "per_ot_day", label: "Per OT Day" },
];

export const RATE_UNITS = [
  { value: "per_hour", label: "Per OT Hour" },
  { value: "per_event", label: "Per OT Event" },
  { value: "per_occurrence", label: "Fixed Amount Per OT Occurrence" },
  { value: "per_half_day", label: "Per Half Day" },
  { value: "per_full_day", label: "Per Full Day" },
  { value: "per_minute", label: "Per Minute" },
];

export const OCCURRENCE_EXPAND_ROLE_OPTIONS = [
  { value: "hr", label: "HR" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "superadmin", label: "Super Admin" },
];

export const DEFAULT_OCCURRENCE_SLOTS = 10;
export const MAX_OCCURRENCE_SLOTS = 62;

export const OT_PAY_RATE_PRESETS = [
  { value: 100, label: "100% — Regular Rate" },
  { value: 125, label: "125% — 1.25 × Regular Rate" },
  { value: 150, label: "150% — 1.5 × Regular Rate" },
  { value: 200, label: "200% — 2 × Regular Rate" },
];

export const OT_ROUND_OPTIONS = [
  { value: "nearest_15", label: "Nearest 15 Minutes" },
  { value: "nearest_30", label: "Nearest 30 Minutes" },
  { value: "exact", label: "Exact Minutes" },
];

export const COMP_OFF_VALIDITY = [30, 60, 90, 180];

export const LIMITS = {
  maxOtPercentage: 200,
  maxFixedOtAmount: 100000,
  maxPremiumMultiplier: 5,
};

export function defaultExtraWorkConfig() {
  return {
    version: 1,
    configured: false,
    sunday: { compensation_method: "normal_salary_comp_off" },
    holiday: { compensation_method: "double_salary" },
    overtime: {
      enabled: false,
      hours_source: "automatic",
      method: "percentage_of_component",
      salary_component: "BASIC",
      salary_component_other: "",
      selected_component_codes: [],
      percentage: 20,
      percentage_applied_on: "per_ot_hour",
      fixed_amount: 0,
      currency: "INR",
      rate_unit: "per_hour",
      occurrence_slots_default: DEFAULT_OCCURRENCE_SLOTS,
      occurrence_expand_roles: ["hr", "manager", "admin", "superadmin"],
      occurrence_enrollments: {},
      working_days: 26,
      working_hours: 8,
      ot_pay_rate_pct: 150,
      formula_expression: "",
      formula_template: "",
      allocation_method: "hours",
      count_per_entry: 1,
      record_hours_with_count: true,
      count_rules: [],
      active_count_rule_id: "",
      department_rates_enabled: false,
      department_rates: [],
      department_hours_enabled: false,
      department_hours: [],
      policy_version: 1,
    },
    premium: {
      sunday_multiplier: 1,
      holiday_multiplier: 2,
      night_shift_multiplier: 1,
      weekend_multiplier: 1,
      special_multiplier: 1,
      festival_multiplier: 1,
    },
    comp_off: {
      grant_enabled: true,
      validity_days: 90,
      expiry_policy: "lapse",
      carry_forward_allowed: false,
      max_balance: 12,
      approval_required: true,
      manager_approval: true,
      hr_approval: false,
      automatic_credit: false,
      automatic_expiry: true,
    },
    eligibility: {
      min_ot_minutes: 30,
      min_ot_hours: 0,
      max_ot_hours_per_day: 4,
      max_ot_hours_per_month: 48,
      round_ot: "nearest_15",
      ignore_below_minutes: 30,
    },
    processing: {
      over_duty_pay_enabled: false,
      weekoff_work_premium_enabled: false,
      require_hr_approval: true,
      require_ot_approval: true,
      ot_hours_allocation_enabled: false,
    },
  };
}

function methodLabel(options, value) {
  return options.find((o) => o.value === value)?.label || "Not selected";
}

function otHourLimits(cfg) {
  const elig = cfg?.eligibility || {};
  const maxDay = Number(elig.max_ot_hours_per_day);
  const maxMonth = Number(elig.max_ot_hours_per_month);
  return {
    maxDay: Number.isFinite(maxDay) && maxDay > 0 ? maxDay : 0,
    maxMonth: Number.isFinite(maxMonth) && maxMonth > 0 ? maxMonth : 0,
  };
}

export function formatOtHourLimit(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "No limit";
  return Number.isInteger(n) ? `${n}h` : `${n}h`;
}

/** Preview hours cannot exceed the same daily and monthly caps payroll uses. */
function previewHoursWithinLimits(sampleHours, cfg) {
  const { maxDay, maxMonth } = otHourLimits(cfg);
  let hours = Number(sampleHours) || 0;
  if (maxDay > 0) hours = Math.min(hours, maxDay);
  if (maxMonth > 0) hours = Math.min(hours, maxMonth);
  return hours;
}

export function formatOtQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/** Convert worked hours into the OT unit selected on Overtime Works. */
export function allocateOtQuantity(rawHours, cfg) {
  const ot = cfg?.overtime || cfg || {};
  const method = ot.allocation_method || "hours";
  const hours = Math.max(0, Number(rawHours) || 0);
  if (hours <= 0) {
    return {
      method,
      hours: 0,
      otCount: 0,
      payableUnits: 0,
      unit: method === "hours" ? "hours" : "ot",
      remainderHours: 0,
      ruleName: "",
    };
  }
  if (method === "hours") {
    return {
      method,
      hours,
      otCount: hours,
      payableUnits: hours,
      unit: "hours",
      remainderHours: 0,
      ruleName: "1 hour = 1 OT hour",
    };
  }
  if (method === "ot_count") {
    const awarded = Number(ot.count_per_entry) > 0 ? Number(ot.count_per_entry) : 1;
    return {
      method,
      hours,
      otCount: awarded,
      payableUnits: awarded,
      unit: "ot",
      remainderHours: 0,
      ruleName: "1 OT = 1 OT",
    };
  }
  const rules = Array.isArray(ot.count_rules) ? ot.count_rules : [];
  const rule = rules.find((item) => item.id === ot.active_count_rule_id) || rules[0];
  const block = Number(rule?.hours) > 0 ? Number(rule.hours) : 1;
  const awarded = Number(rule?.ot_count) > 0 ? Number(rule.ot_count) : 1;
  const blocks = Math.floor(hours / block);
  const count = blocks * awarded;
  const remainder = Math.round((hours - blocks * block) * 100) / 100;
  return {
    method: "custom",
    hours,
    otCount: count,
    payableUnits: count,
    unit: "ot",
    remainderHours: remainder > 0 ? remainder : 0,
    ruleName: rule?.name || "Custom OT count",
    blockHours: block,
    blockCount: awarded,
  };
}

export function allocationMethodLabel(cfg) {
  const ot = cfg?.overtime || {};
  const method = ot.allocation_method || "hours";
  if (method === "custom") {
    const sample = allocateOtQuantity(1, cfg);
    return sample.ruleName ? `Custom · ${sample.ruleName}` : "Custom OT count";
  }
  return OT_ALLOCATION_METHODS.find((item) => item.value === method)?.label || "Count by hours";
}

/** Live calculation preview for HR users (plain language). */
export function buildOtPayPreview(cfg, { sampleHours = 4, sampleEvents = 3, sampleBasic = 26000 } = {}) {
  const ot = cfg?.overtime || {};
  const method = ot.method || "percentage_of_component";
  const days = Number(ot.working_days) || 26;
  const hours = Number(ot.working_hours) || 8;
  const basis = Number(sampleBasic) || 26000;
  const { maxDay, maxMonth } = otHourLimits(cfg);
  const cappedHours = previewHoursWithinLimits(sampleHours, cfg);
  const alloc = allocateOtQuantity(cappedHours, cfg);
  const countMode = alloc.unit === "ot";
  const qty = countMode ? alloc.payableUnits : cappedHours;
  const limitLines = [
    { label: "Maximum OT Per Day", value: formatOtHourLimit(maxDay) },
    { label: "Maximum OT Per Month", value: formatOtHourLimit(maxMonth) },
  ];
  const allocationLines = [
    { label: "Worked Hours", value: `${formatOtQty(cappedHours)}h` },
    { label: "Allocation", value: allocationMethodLabel(cfg) },
    {
      label: countMode ? "Allocated OT Count" : "OT Hours",
      value: countMode ? formatOtQty(qty) : `${formatOtQty(cappedHours)}h`,
    },
    ...(countMode && alloc.remainderHours > 0
      ? [{ label: "Hours left on the record", value: `${formatOtQty(alloc.remainderHours)}h` }]
      : []),
  ];
  const lines = [];

  if (method === "hourly_rate") {
    const regular = days > 0 && hours > 0 ? basis / (days * hours) : 0;
    const payPct = Number(ot.ot_pay_rate_pct) || 100;
    const otRate = regular * (payPct / 100);
    const earnings = otRate * qty;
    lines.push(
      { label: "Salary Basis", value: `₹${basis.toLocaleString("en-IN")}` },
      { label: "Working Days", value: String(days) },
      { label: "Working Hours", value: String(hours) },
      { label: "Regular Hourly Rate", value: `₹${regular.toFixed(2)}` },
      { label: "OT Pay Rate", value: `${payPct}%` },
      { label: countMode ? "OT Earnings / OT" : "OT Earnings / Hour", value: `₹${otRate.toFixed(2)}` },
      ...allocationLines,
      ...limitLines,
      { label: "Estimated OT Earnings", value: `₹${earnings.toFixed(2)}`, emphasize: true }
    );
    return { lines, amount: earnings };
  }

  if (method === "fixed_rate") {
    const amt = Number(ot.fixed_amount) || 0;
    if (ot.rate_unit === "per_event" || ot.rate_unit === "per_occurrence") {
      const earnings = amt * sampleEvents;
      const unitLabel = ot.rate_unit === "per_occurrence" ? "Occurrence" : "Event";
      lines.push(
        { label: `Approved OT ${unitLabel}s`, value: String(sampleEvents) },
        { label: `OT Amount Per ${unitLabel}`, value: `₹${amt.toLocaleString("en-IN")}` },
        ...limitLines,
        { label: "OT Earnings", value: `₹${earnings.toLocaleString("en-IN")}`, emphasize: true }
      );
      return { lines, amount: earnings };
    }
    const earnings = amt * qty;
    lines.push(
      ...allocationLines,
      { label: countMode ? "Amount / OT" : "Amount / Hour", value: `₹${amt.toLocaleString("en-IN")}` },
      ...limitLines,
      { label: "OT Earnings", value: `₹${earnings.toLocaleString("en-IN")}`, emphasize: true }
    );
    return { lines, amount: earnings };
  }

  if (method === "percentage_of_component") {
    const pct = Number(ot.percentage) || 0;
    const applied = ot.percentage_applied_on || "per_ot_hour";
    let unit = 0;
    if (applied === "monthly_salary") unit = (basis * pct) / 100 / (days * hours);
    else if (applied === "daily_salary") unit = ((basis / days) * pct) / 100 / hours;
    else if (applied === "per_ot_day") unit = ((basis / days) * pct) / 100;
    else unit = (basis / (days * hours)) * (pct / 100);
    const dayQty = countMode ? qty : 1;
    const hourQty = countMode ? qty : cappedHours;
    const earnings = applied === "per_ot_day" ? unit * dayQty : unit * hourQty;
    lines.push(
      { label: "Base Amount", value: `₹${basis.toLocaleString("en-IN")}` },
      { label: "Percentage", value: `${pct}%` },
      { label: "Applied On", value: methodLabel(PERCENTAGE_APPLIED_ON, applied) },
      ...allocationLines,
      ...limitLines,
      { label: "Calculated OT Amount", value: `₹${earnings.toFixed(2)}`, emphasize: true }
    );
    return { lines, amount: earnings };
  }

  if (method === "formula") {
    const expr = String(ot.formula_expression || "").trim();
    const payPct = Number(ot.ot_pay_rate_pct) || 100;
    const hourly = days > 0 && hours > 0 ? basis / (days * hours) : 0;
    const evaluated = expr
      ? evaluateOtFormula(expr, {
          BASIC: basis,
          GROSS: basis,
          DA: 0,
          HOURLY_RATE: hourly,
          FIXED_OT_AMOUNT: Number(ot.fixed_amount) || 0,
          OT_HOURS: qty,
          OT_COUNT: qty,
          OT_DAYS: countMode ? qty : 1,
          OT_EVENTS: sampleEvents,
          WORKING_DAYS: days,
          WORKING_HOURS: hours,
          OT_PAY_RATE: payPct,
        })
      : { ok: false, error: "Not set" };
    lines.push(
      { label: "Formula", value: expr ? readableOtFormula(expr) : "(not set)" },
      { label: countMode ? "OT Count (input)" : "OT Hours (input)", value: formatOtQty(qty) },
      ...allocationLines,
      ...limitLines,
      {
        label: "Estimated OT Earnings",
        value: evaluated.ok ? `₹${Number(evaluated.value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (expr ? evaluated.error : "Open Formula Builder and save a formula"),
        emphasize: true,
      }
    );
    return { lines, amount: evaluated.ok ? evaluated.value : null };
  }

  return { lines: [], amount: 0 };
}

export function extraWorkSummary(cfg) {
  const c = cfg || defaultExtraWorkConfig();
  const ot = c.overtime || {};
  let otMethod = methodLabel(OT_METHODS, ot.method);
  if (ot.method === "percentage_of_component") {
    const comp = SALARY_COMPONENTS.find((x) => x.value === ot.salary_component)?.label || ot.salary_component;
    otMethod = `${ot.percentage || 0}% of ${comp}`;
  } else if (ot.method === "hourly_rate") {
    otMethod = `Hourly @ ${ot.ot_pay_rate_pct || 100}% OT Pay Rate`;
  } else if (ot.method === "fixed_rate") {
    const unit = RATE_UNITS.find((x) => x.value === ot.rate_unit)?.label || ot.rate_unit;
    otMethod = `₹${ot.fixed_amount || 0} ${unit}`;
  } else if (ot.method === "formula") {
    otMethod = ot.formula_expression ? `Formula: ${ot.formula_expression}` : "Custom Formula";
  }
  return {
    otEnabled: ot.enabled ? "Enabled" : "Disabled",
    hoursSource: methodLabel(OT_HOURS_SOURCES, ot.hours_source || "automatic"),
    allocation: allocationMethodLabel(c),
    sunday: methodLabel(SUNDAY_METHODS, c.sunday?.compensation_method),
    holiday: methodLabel(HOLIDAY_METHODS, c.holiday?.compensation_method),
    otMethod,
    maxOtPerDay:
      (ot.hours_source || "automatic") === "manual"
        ? "Not applied"
        : formatOtHourLimit(c.eligibility?.max_ot_hours_per_day),
    maxOtPerMonth:
      (ot.hours_source || "automatic") === "manual"
        ? "Not applied"
        : formatOtHourLimit(c.eligibility?.max_ot_hours_per_month),
    departmentRates: ot.department_rates_enabled
      ? `On · ${(ot.department_rates || []).length} set`
      : "Off — organisation rate",
    holidayMultiplier: `${Number(c.premium?.holiday_multiplier || 0).toFixed(1)}x`,
    sundayMultiplier: `${Number(c.premium?.sunday_multiplier || 0).toFixed(1)}x`,
    compOff: c.comp_off?.grant_enabled ? "Enabled" : "Disabled",
    validity: c.comp_off?.grant_enabled ? `${c.comp_off?.validity_days || 0} Days` : "—",
    hrApproval: c.processing?.require_hr_approval ? "Required" : "Not required",
  };
}

const OT_ERROR_KEYS = new Set([
  "ot_hours_source",
  "ot_allocation_method",
  "ot_count_per_entry",
  "ot_count_rules",
  "ot_method",
  "ot_percentage",
  "ot_component_other",
  "ot_selected_components",
  "ot_percentage_applied_on",
  "ot_working_days",
  "ot_working_hours",
  "ot_pay_rate_pct",
  "ot_department_rates",
  "ot_department_hours",
  "ot_fixed_amount",
  "ot_rate_unit",
  "ot_occurrence_slots",
  "ot_occurrence_roles",
  "ot_formula",
  "min_ot_minutes",
  "ignore_below_minutes",
  "max_ot_hours_per_day",
  "max_ot_hours_per_month",
]);

const PREMIUM_ERROR_KEYS = new Set([
  "sunday_method",
  "holiday_method",
  "sunday_multiplier",
  "holiday_multiplier",
  "night_shift_multiplier",
  "weekend_multiplier",
  "special_multiplier",
  "festival_multiplier",
  "validity_days",
]);

function pickErrors(errors, allowed) {
  const next = {};
  Object.entries(errors || {}).forEach(([k, v]) => {
    if (allowed.has(k)) next[k] = v;
  });
  return next;
}

export function validateExtraWorkConfig(cfg) {
  const errors = {};
  const c = cfg || {};
  if (!c.sunday?.compensation_method) {
    errors.sunday_method = "Sunday Work Compensation Method is required.";
  }
  if (!c.holiday?.compensation_method) {
    errors.holiday_method = "Holiday Work Compensation Method is required.";
  }
  const ot = c.overtime || {};

  // When OT is off, skip OT pay field validation (still allow save).
  if (ot.enabled) {
    if (!ot.hours_source) errors.ot_hours_source = "Select how OT hours should be determined.";
    if (!["hours", "ot_count", "custom"].includes(ot.allocation_method || "hours")) {
      errors.ot_allocation_method = "Select how overtime should be allocated.";
    }
    if ((ot.allocation_method || "hours") === "ot_count" && !(Number(ot.count_per_entry) > 0)) {
      errors.ot_count_per_entry = "OT count per entry must be greater than zero.";
    }
    if ((ot.allocation_method || "hours") === "custom") {
      const rules = Array.isArray(ot.count_rules) ? ot.count_rules : [];
      if (!rules.length) {
        errors.ot_count_rules = "Add at least one OT count method.";
      } else if (rules.some((rule) => !String(rule.name || "").trim() || !(Number(rule.hours) > 0) || !(Number(rule.ot_count) > 0))) {
        errors.ot_count_rules = "Each OT count method needs a name, hours greater than zero, and an OT count greater than zero.";
      }
    }
    if (!ot.method) errors.ot_method = "OT Pay Calculation Method is required.";

    if (ot.method === "percentage_of_component") {
      const pct = Number(ot.percentage);
      if (!Number.isFinite(pct) || pct <= 0) errors.ot_percentage = "OT percentage must be greater than zero.";
      if (pct > LIMITS.maxOtPercentage) errors.ot_percentage = `Percentage cannot exceed ${LIMITS.maxOtPercentage}%.`;
      if (ot.salary_component === "OTHER" && !String(ot.salary_component_other || "").trim()) {
        errors.ot_component_other = "Enter the salary component code for other earnings.";
      }
      if (ot.salary_component === "SELECTED" && !(ot.selected_component_codes || []).length) {
        errors.ot_selected_components = "Select at least one salary component.";
      }
      if (!ot.percentage_applied_on) errors.ot_percentage_applied_on = "Choose what the percentage applies to.";
    }

    if (ot.method === "hourly_rate") {
      if (!(Number(ot.working_days) > 0)) errors.ot_working_days = "Working days must be greater than zero.";
      if (!(Number(ot.working_hours) > 0)) errors.ot_working_hours = "Working hours must be greater than zero.";
      if (!(Number(ot.ot_pay_rate_pct) > 0)) errors.ot_pay_rate_pct = "OT Pay Rate (%) must be greater than zero.";
      if (ot.salary_component === "SELECTED" && !(ot.selected_component_codes || []).length) {
        errors.ot_selected_components = "Select at least one salary component.";
      }
      if (ot.salary_component === "OTHER" && !String(ot.salary_component_other || "").trim()) {
        errors.ot_component_other = "Enter the salary component code for other earnings.";
      }
    }

    if (ot.method === "fixed_rate") {
      const amt = Number(ot.fixed_amount);
      if (!Number.isFinite(amt) || amt < 0) errors.ot_fixed_amount = "Fixed OT amount cannot be negative.";
      if (amt === 0) errors.ot_fixed_amount = "Fixed OT amount must be greater than zero.";
      if (amt > LIMITS.maxFixedOtAmount) errors.ot_fixed_amount = "Fixed OT amount exceeds the configured business limit.";
      if (!ot.rate_unit) errors.ot_rate_unit = "Select Per OT Hour, Per OT Event, or Fixed Amount Per OT Occurrence.";
      if (ot.rate_unit === "per_occurrence") {
        const slots = Number(ot.occurrence_slots_default);
        if (!Number.isFinite(slots) || slots < 1 || slots > MAX_OCCURRENCE_SLOTS) {
          errors.ot_occurrence_slots = `Default occurrence checkboxes must be between 1 and ${MAX_OCCURRENCE_SLOTS}.`;
        }
        if (!(ot.occurrence_expand_roles || []).length) {
          errors.ot_occurrence_roles = "Select at least one role that may increase occurrence checkboxes.";
        }
      }
    }

    const deptRows = Array.isArray(ot.department_rates) ? ot.department_rates : [];
    const deptLimit = ot.method === "percentage_of_component" ? LIMITS.maxOtPercentage : 500;
    for (const row of deptRows) {
      if (ot.method === "fixed_rate") {
        if (row.fixed_amount == null || row.fixed_amount === "") continue;
        const deptAmt = Number(row.fixed_amount);
        if (!(deptAmt > 0) || deptAmt > LIMITS.maxFixedOtAmount) {
          errors.ot_department_rates = "Each department OT amount must be greater than zero and within the business limit.";
          break;
        }
      } else if (row.rate_pct != null && row.rate_pct !== "") {
        const pct = Number(row.rate_pct);
        if (!(pct > 0) || pct > deptLimit) {
          errors.ot_department_rates = `Each department OT rate must be greater than zero and at most ${deptLimit}%.`;
          break;
        }
      }
    }

    for (const row of Array.isArray(ot.department_hours) ? ot.department_hours : []) {
      if (row.working_hours == null || row.working_hours === "") continue;
      const hours = Number(row.working_hours);
      if (!(hours > 0) || hours > 24) {
        errors.ot_department_hours = "Each department working day must be greater than zero and at most 24 hours.";
        break;
      }
    }

    if (ot.method === "formula" && !String(ot.formula_expression || "").trim()) {
      errors.ot_formula = "Open Formula Builder and save a custom OT formula.";
    }
  }

  const prem = c.premium || {};
  const multipliers = [
    ["sunday_multiplier", "Sunday Premium Multiplier"],
    ["holiday_multiplier", "Holiday Premium Multiplier"],
    ["night_shift_multiplier", "Night Shift Premium"],
    ["weekend_multiplier", "Weekend Premium"],
    ["special_multiplier", "Special Premium"],
    ["festival_multiplier", "Festival Premium"],
  ];
  multipliers.forEach(([key, label]) => {
    const v = Number(prem[key]);
    if (!Number.isFinite(v) || v <= 0) errors[key] = `${label} cannot be zero.`;
    if (v > LIMITS.maxPremiumMultiplier) errors[key] = `${label} cannot exceed ${LIMITS.maxPremiumMultiplier}x.`;
  });
  const co = c.comp_off || {};
  if (!(Number(co.validity_days) > 0)) errors.validity_days = "Comp-Off validity must be greater than zero.";
  const hoursSource = String(c.overtime?.hours_source || "automatic");
  if (hoursSource !== "manual") {
    const elig = c.eligibility || {};
    const minHours = Math.max(Number(elig.min_ot_hours) || 0, (Number(elig.min_ot_minutes) || 0) / 60);
    const maxDay = Number(elig.max_ot_hours_per_day);
    const maxMonth = Number(elig.max_ot_hours_per_month);
    if (Number(elig.min_ot_minutes) < 0) errors.min_ot_minutes = "Minimum OT minutes cannot be negative.";
    if (Number(elig.ignore_below_minutes) < 0) errors.ignore_below_minutes = "Ignore-below threshold cannot be negative.";
    if (maxDay < 0) errors.max_ot_hours_per_day = "Maximum OT per day cannot be negative.";
    if (maxMonth < 0) errors.max_ot_hours_per_month = "Maximum OT per month cannot be negative.";
    if (maxDay > 0 && minHours > maxDay) {
      errors.max_ot_hours_per_day = "Minimum OT hours cannot be greater than maximum OT per day.";
    }
    if (maxMonth > 0 && maxDay > 0 && maxDay > maxMonth) {
      errors.max_ot_hours_per_month = "Maximum OT per day cannot be greater than maximum OT per month.";
    }
  }
  return errors;
}

/** Validate only Overtime Works fields (OT pay + eligibility). */
export function validateOvertimeConfig(cfg) {
  return pickErrors(validateExtraWorkConfig(cfg), OT_ERROR_KEYS);
}

/** Validate only Premium Works fields (Sunday / holiday / multipliers / Comp-Off). */
export function validatePremiumConfig(cfg) {
  return pickErrors(validateExtraWorkConfig(cfg), PREMIUM_ERROR_KEYS);
}

/** Merge OT section defaults onto an existing full config (keeps premium sections). */
export function withDefaultOvertimeSections(cfg) {
  const d = defaultExtraWorkConfig();
  const base = cfg || d;
  return {
    ...base,
    overtime: { ...d.overtime },
    eligibility: { ...d.eligibility },
    processing: {
      ...(base.processing || {}),
      ot_hours_allocation_enabled: d.processing.ot_hours_allocation_enabled,
      require_ot_approval: d.processing.require_ot_approval,
    },
  };
}

/** Merge premium section defaults onto an existing full config (keeps OT sections). */
export function withDefaultPremiumSections(cfg) {
  const d = defaultExtraWorkConfig();
  const base = cfg || d;
  return {
    ...base,
    sunday: { ...d.sunday },
    holiday: { ...d.holiday },
    premium: { ...d.premium },
    comp_off: { ...d.comp_off },
    processing: {
      ...(base.processing || {}),
      over_duty_pay_enabled: d.processing.over_duty_pay_enabled,
      weekoff_work_premium_enabled: d.processing.weekoff_work_premium_enabled,
      require_hr_approval: d.processing.require_hr_approval,
    },
  };
}

export function configsEqual(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
